/**
 * Typed HTTP layer for the admin app.
 *
 * These are thin request functions only — caching, retries, and invalidation
 * belong to TanStack Query hooks that wrap them.
 *
 * Paths are relative. In development Vite proxies /api/admin to uvicorn (see
 * vite.config.ts); in production this app is served under its own path with
 * /api/admin reachable same-origin (see admin/README.md for the deploy note).
 */

import type { AdminProfile, AdminUserPage, DashboardStats, Plan, Token } from './types'

/* --------------------------------------------------------------- token --- */
/**
 * Separate storage key from the main app's `sai_token` — an admin token and
 * a user token must never collide or overwrite each other, since the two
 * apps can be open in the same browser at once.
 */
const TOKEN_KEY = 'sai_admin_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/* --------------------------------------------------------------- error --- */
/** Normalised failure. `status` is the HTTP code, or 0 when the request never
 * reached the server (offline, DNS, CORS, aborted). */
export class ApiError extends Error {
  readonly status: number
  readonly detail: unknown

  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }

  get isAuthError(): boolean {
    return this.status === 401
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }
}

/** FastAPI returns `{detail: string}` or, for 422, `{detail: [{loc, msg, …}]}`. */
function messageFromBody(body: unknown, status: number): string {
  if (typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string }
      if (typeof first?.msg === 'string') return first.msg
    }
  }
  return `Request failed (${status}).`
}

/* ------------------------------------------------------------- request --- */
interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options
  const headers: Record<string, string> = {}

  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'Cannot reach the server. Check your connection.', cause)
  }

  if (!response.ok) {
    const parsed = await response.json().catch(() => null)
    throw new ApiError(response.status, messageFromBody(parsed, response.status), parsed)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/* ---------------------------------------------------------------- auth --- */
export const auth = {
  login: (email: string, password: string) =>
    request<Token>('/api/admin/auth/login', { method: 'POST', body: { email, password } }),

  me: () => request<AdminProfile>('/api/admin/auth/me'),

  updateName: (name: string) =>
    request<AdminProfile>('/api/admin/auth/me', { method: 'PATCH', body: { name } }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ detail: string }>('/api/admin/auth/me/password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),
}

/* ----------------------------------------------------------- dashboard --- */
export const dashboard = {
  stats: () => request<DashboardStats>('/api/admin/dashboard'),
}

/* ---------------------------------------------------------------- users --- */
export const adminUsers = {
  list: (page: number, pageSize: number) =>
    request<AdminUserPage>(`/api/admin/users/?page=${page}&page_size=${pageSize}`),

  changePlan: (userId: string, planId: string) =>
    request(`/api/admin/users/${userId}/plan`, { method: 'PATCH', body: { plan_id: planId } }),

  changeLimits: (
    userId: string,
    limits: { file_limit?: number; statements_limit?: number; questions_limit?: number },
  ) => request(`/api/admin/users/${userId}/limits`, { method: 'PATCH', body: limits }),
}

/* ---------------------------------------------------------------- plans --- */
/**
 * BACKEND GAP: GET /api/admin/plans does not exist yet — the only current
 * route (GET /api/plans) requires a user token, which this app never has.
 * You're adding an admin-scoped route yourself; this client call is wired
 * against that URL ahead of time so nothing here needs to change once it
 * exists. See the "change plan" dialog for how the 404 is surfaced in the
 * meantime.
 */
export const plans = {
  list: () => request<Plan[]>('/api/admin/plans'),
}
